import { useSearchParams } from 'react-router';
import { startGoogleSignIn } from '@/client/session';

/** `/signin` — the signed-out surface (ESC-005, WP-02).
 *
 *  The design package has no sign-in screen among its 31, so this is the one screen in the product with no pixel
 *  authority behind it. That makes the rule for it stricter rather than looser: nothing here is invented visual
 *  language. Every value is a token the shell already uses — the same ground, the same card grammar as the portal,
 *  the same 13.5px/700 lockup the sidebar renders the product name in, the same accent the app uses for its one
 *  primary action. A person arriving here should recognise where they are before they read a word.
 *
 *  It also renders with **no data**. It sits outside `DataProvider` and outside the session guard, because the whole
 *  point of it is to be the thing an unauthenticated visitor can see, and a sign-in screen that needs a session to
 *  render is a redirect loop.
 *
 *  Recorded as the owner's recommended default under OD-07. Its visual baseline is self-frozen like the other
 *  composed surfaces and is provisional until approved (ESC-003).
 */

/** What the server can tell us, in the server's own vocabulary. Anything else is treated as a generic failure
 *  rather than echoed — the query string is attacker-controlled, and rendering arbitrary text from it is how a
 *  sign-in screen ends up displaying someone else's message. */
const REASONS: Record<string, { title: string; detail: string; tone: 'neg' | 'amb' }> = {
  declined: {
    title: 'Sign-in was cancelled',
    detail: 'You chose not to continue at Google. Nothing was shared and no account was created.',
    tone: 'amb',
  },
  not_invited: {
    title: 'That account is not on the invited list',
    detail:
      'This is a private test environment and access is limited to named accounts. If you think this is a mistake, ask whoever invited you to add the address you signed in with.',
    tone: 'neg',
  },
  unavailable: {
    title: 'Sign-in is unavailable',
    detail:
      'The identity provider could not be reached. This is on our side — trying again shortly usually works.',
    tone: 'neg',
  },
  failed: {
    title: 'Sign-in could not be completed',
    detail:
      'The sign-in did not finish. Starting again is the fastest fix; the link may simply have expired.',
    tone: 'neg',
  },
};

export function SignedOutPage() {
  const [params] = useSearchParams();
  const reason = REASONS[params.get('error') ?? ''];
  const signedOut = params.get('signedout') === '1';
  // Where they were going. The server validates this again on the way through, because a value that has been to the
  // browser and back is a value an attacker has had their hands on.
  const next = params.get('next') ?? '/home';

  return (
    <div
      data-page="signin"
      style={{
        minHeight: '100vh',
        background: 'var(--bg)',
        color: 'var(--text)',
        fontSize: '13px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px 16px',
      }}
    >
      <div
        style={{ width: '100%', maxWidth: '420px', display: 'flex', flexDirection: 'column', gap: '18px' }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
          <div style={{ fontSize: '13.5px', fontWeight: 700, letterSpacing: '-0.01em' }}>PointFive OS</div>
          <div style={{ fontSize: '10.5px', color: 'var(--muted)' }}>Trading operating system</div>
        </div>

        <div
          style={{
            background: 'var(--surface)',
            border: '1px solid var(--card-line)',
            boxShadow: 'var(--shadow-card)',
            borderRadius: '16px',
            padding: '22px 22px 20px',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
          }}
        >
          {reason ? (
            <div
              data-signin-error={params.get('error') ?? ''}
              style={{
                border: `1px solid color-mix(in srgb, var(--${reason.tone}) 40%, transparent)`,
                background: `color-mix(in srgb, var(--${reason.tone}) 9%, transparent)`,
                borderRadius: '10px',
                padding: '11px 13px',
                display: 'flex',
                flexDirection: 'column',
                gap: '4px',
              }}
            >
              <div style={{ fontSize: '12.5px', fontWeight: 600, color: `var(--${reason.tone})` }}>
                {reason.title}
              </div>
              <div style={{ fontSize: '11.5px', color: 'var(--text-mid)', lineHeight: 1.5 }}>
                {reason.detail}
              </div>
            </div>
          ) : null}

          {signedOut && !reason ? (
            <div
              data-signin-notice="signedout"
              style={{ fontSize: '11.5px', color: 'var(--muted)', lineHeight: 1.5 }}
            >
              You are signed out. Your session has been revoked on this device.
            </div>
          ) : null}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
            <div style={{ fontSize: '15px', fontWeight: 700, letterSpacing: '-0.01em' }}>Sign in</div>
            <div style={{ fontSize: '11.5px', color: 'var(--muted)', lineHeight: 1.5 }}>
              Use the Google account you were invited with.
            </div>
          </div>

          <button
            type="button"
            data-signin-google
            onClick={() => startGoogleSignIn(next)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '9px',
              width: '100%',
              padding: '11px 18px',
              borderRadius: '10px',
              border: '1px solid color-mix(in srgb, var(--acc) 55%, transparent)',
              background: 'color-mix(in srgb, var(--acc) 12%, transparent)',
              color: 'var(--acc)',
              fontSize: '12.5px',
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            <i className="ph ph-google-logo" style={{ fontSize: '15px' }} aria-hidden="true"></i>
            Continue with Google
          </button>
        </div>

        <div style={{ fontSize: '10.5px', color: 'var(--faint)', lineHeight: 1.6 }}>
          This is a private test environment. Trading is simulated and no real orders are placed. By signing
          in you accept the{' '}
          <a href="/legal/terms.html" target="_blank" rel="noreferrer" style={{ color: 'var(--muted2)' }}>
            Terms
          </a>{' '}
          and{' '}
          <a href="/legal/privacy.html" target="_blank" rel="noreferrer" style={{ color: 'var(--muted2)' }}>
            Privacy Policy
          </a>{' '}
          (drafts, pending counsel review); the version you accept is recorded with your account.
        </div>
      </div>
    </div>
  );
}
